import { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../../../../services/api';
import { useI18n } from '../../../../contexts/I18nContext';
import { DICT_NAMES } from '../../../../lib/constants';
import {
  formatModelOptions,
  normalizeModelKey,
  normalizeModelValue,
  resolveModelOption
} from './modelOptions';

export const useSizeSelector = ({ value, onChange }) => {
  const { language } = useI18n();
  const [model, setModel] = useState('');
  const [aspectRatio, setAspectRatio] = useState('');
  const [resolution, setResolution] = useState('');

  const [modelOptions, setModelOptions] = useState([]);
  const [aspectRatioOptions, setAspectRatioOptions] = useState([]);
  const [resolutionOptions, setResolutionOptions] = useState([]);
  const lastEmittedRef = useRef('');
  const onChangeRef = useRef(onChange);
  const modelRef = useRef('');
  const aspectRatioRef = useRef('');
  const resolutionRef = useRef('');

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const fetchDict = useCallback(
    async (dictName) => {
      const lang = (language ?? '').toString();
      const apiLanguage =
        lang === 'en'
          ? 'en'
          : lang === 'zh-TW' || lang.toLowerCase().includes('tw')
            ? 'tc'
            : 'sc';
      const response = await axios.get('/dictDetail', {
        params: {
          dictName,
          language: apiLanguage,
          page: 0,
          limit: 100
        }
      });
      return Array.isArray(response.data?.data) ? response.data.data : [];
    },
    [language]
  );

  const updateParent = useCallback((m, a, r) => {
    const cb = onChangeRef.current;
    if (!cb) return;
    const payload = {
      model: normalizeModelValue(m),
      aspectRatio: a,
      resolution: r
    };
    const signature = JSON.stringify(payload);
    if (signature === lastEmittedRef.current) return;
    lastEmittedRef.current = signature;
    cb(payload);
  }, []);

  useEffect(() => {
    if (value && typeof value === 'object') {
      const rawModel = value.model;
      if (rawModel !== undefined && rawModel !== null && `${rawModel}`.trim() !== '') {
        setModel(normalizeModelValue(rawModel));
      }
      const rawAspect = value.aspectRatio;
      if (rawAspect !== undefined && rawAspect !== null && `${rawAspect}`.trim() !== '') {
        setAspectRatio(`${rawAspect}`.trim());
      }
      const rawRes = value.resolution;
      if (rawRes !== undefined && rawRes !== null && `${rawRes}`.trim() !== '') {
        setResolution(`${rawRes}`.trim());
      }
      return;
    }
    if (typeof value === 'string' && value) {
      setAspectRatio(value.toString().trim());
    }
  }, [value]);

  useEffect(() => {
    aspectRatioRef.current = aspectRatio;
    resolutionRef.current = resolution;
  }, [aspectRatio, resolution]);

  useEffect(() => {
    modelRef.current = normalizeModelValue(model);
  }, [model]);

  useEffect(() => {
    let isMounted = true;

    const loadModels = async () => {
      const list = await fetchDict(DICT_NAMES.MODEL).catch(() => []);
      if (!isMounted) return;

      setModelOptions(formatModelOptions(list));
    };

    loadModels();
    return () => {
      isMounted = false;
    };
  }, [fetchDict]);

  useEffect(() => {
    const resolved = resolveModelOption(model, modelOptions);
    if (!resolved) {
      if (modelOptions.length > 0) {
        const nextModel = modelOptions[0].value;
        if (normalizeModelKey(model) !== normalizeModelKey(nextModel)) {
          setModel(nextModel);
          setAspectRatio('');
          setResolution('');
        }
      }
      return;
    }

    const suffix = resolved.suffix || resolved.value || resolved.label;
    if (!suffix) return;

    let isMounted = true;
    const loadDependentOptions = async () => {
      const primaryAspectDictName = `${DICT_NAMES.ASPECT_RATIO_PREFIX}${suffix}`;
      const fallbackAspectDictName = `mageAspectRatio_${suffix}`;
      const resolutionDictName = `${DICT_NAMES.RESOLUTION_PREFIX}${suffix}`;

      const [primaryRatios, resolutions] = await Promise.all([
        fetchDict(primaryAspectDictName).catch(() => []),
        fetchDict(resolutionDictName).catch(() => [])
      ]);

      const ratios = primaryRatios.length > 0
        ? primaryRatios
        : await fetchDict(fallbackAspectDictName).catch(() => []);

      if (!isMounted) return;

      const ratioOpts = ratios
        .map(item => ({ value: item.value, label: item.label }))
        .filter(o => o.value);
      const resOpts = resolutions
        .map(item => ({ value: item.value, label: item.label }))
        .filter(o => o.value);

      setAspectRatioOptions(ratioOpts);
      setResolutionOptions(resOpts);

      let nextAspectRatio = aspectRatioRef.current;
      let nextResolution = resolutionRef.current;

      if (!nextAspectRatio || !ratioOpts.find(o => o.value === nextAspectRatio)) {
        nextAspectRatio = ratioOpts.length > 0 ? ratioOpts[0].value : '';
      }
      
      // 默认解析度逻辑：优先使用 '2K'，如果不存在则使用列表第一项
      if (!nextResolution || !resOpts.find(o => o.value === nextResolution)) {
        const default2K = resOpts.find(o => (o.value || '').toUpperCase().includes('2K'));
        if (default2K) {
            nextResolution = default2K.value;
        } else {
            nextResolution = resOpts.length > 0 ? resOpts[0].value : '';
        }
      }

      if (nextAspectRatio !== aspectRatioRef.current) setAspectRatio(nextAspectRatio);
      if (nextResolution !== resolutionRef.current) setResolution(nextResolution);

      updateParent(resolved.value, nextAspectRatio, nextResolution);
    };

    loadDependentOptions();
    return () => {
      isMounted = false;
    };
  }, [model, modelOptions, fetchDict, updateParent]);

  const handleModelChange = (newModel) => {
    const nextModel = normalizeModelValue(newModel);
    const currentModel = modelRef.current || model;
    if (normalizeModelKey(nextModel) === normalizeModelKey(currentModel)) return;
    setModel(nextModel);
    setAspectRatio('');
    setResolution('');
  };

  const handleAspectRatioChange = (newRatio) => {
    setAspectRatio(newRatio);
    updateParent(modelRef.current, newRatio, resolutionRef.current);
  };

  const handleResolutionChange = (newRes) => {
    setResolution(newRes);
    updateParent(modelRef.current, aspectRatioRef.current, newRes);
  };

  return {
    model: normalizeModelValue(model),
    aspectRatio,
    resolution,
    modelOptions,
    aspectRatioOptions,
    resolutionOptions,
    handleModelChange,
    handleAspectRatioChange,
    handleResolutionChange
  };
};
